import { useState, useEffect, useMemo, useCallback } from 'react';

// Types
export interface TimeRange {
    open: string; // HH:mm format (24-hour)
    close: string;
}

export interface DaySchedule {
    isOpen: boolean;
    hours?: TimeRange;
}

export interface HolidaySchedule {
    [date: string]: DaySchedule; // date format: 'MM-DD' or 'YYYY-MM-DD'
}

export interface CallCenterConfig {
    displayCutoffMinutes: number;
    regularHours: {
        monday: DaySchedule;
        tuesday: DaySchedule;
        wednesday: DaySchedule;
        thursday: DaySchedule;
        friday: DaySchedule;
        saturday: DaySchedule;
        sunday: DaySchedule;
    };
    holidayHours: HolidaySchedule;
    timezone: string;
}

export interface CallCenterStatus {
    isCallCenterOpen: boolean;
    isCallCenterClosed: boolean;
    isTextChatAvailable: boolean;
    isHoliday: boolean;
}

interface TodayScheduleInfo {
    schedule: DaySchedule;
    isHoliday: boolean;
    openTime: Date | null;
    closeTime: Date | null;
    cutoffTime: Date | null;
}

// Reusable schedule patterns
const WEEKDAY_HOURS: DaySchedule = { isOpen: true, hours: { open: '09:30', close: '20:10' } };
const SATURDAY_HOURS: DaySchedule = { isOpen: true, hours: { open: '09:30', close: '20:10' } };
const SUNDAY_HOURS: DaySchedule = { isOpen: true, hours: { open: '08:30', close: '18:45' } };
const CLOSED: DaySchedule = { isOpen: false };

// Default configuration
export const DEFAULT_CONFIG: CallCenterConfig = {
    displayCutoffMinutes: 15,
    regularHours: {
        monday: WEEKDAY_HOURS,
        tuesday: WEEKDAY_HOURS,
        wednesday: WEEKDAY_HOURS,
        thursday: WEEKDAY_HOURS,
        friday: WEEKDAY_HOURS,
        saturday: SATURDAY_HOURS,
        sunday: SUNDAY_HOURS,
    },
    holidayHours: {
        '12-24': { isOpen: true, hours: { open: '08:00', close: '16:00' } }, // Christmas Eve
        '12-25': CLOSED, // Christmas Day
        '12-31': { isOpen: true, hours: { open: '08:00', close: '18:00' } }, // New Year's Eve
    },
    timezone: 'America/Chicago',
};

// Helper functions
const parseTime = (timeStr: string, baseDate: Date, timezone: string): Date => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    const dateStr = baseDate.toLocaleDateString('en-CA', { timeZone: timezone });
    const [year, month, day] = dateStr.split('-').map(Number);

    const date = new Date(Date.UTC(year, month - 1, day, hours, minutes));
    const tzDate = new Date(date.toLocaleString('en-US', { timeZone: timezone }));
    const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
    const offset = utcDate.getTime() - tzDate.getTime();

    return new Date(date.getTime() + offset);
};

const getDateKey = (date: Date, timezone: string): string => {
    const formatted = date.toLocaleDateString('en-US', {
        timeZone: timezone,
        month: '2-digit',
        day: '2-digit',
    });
    const [month, day] = formatted.split('/');
    return `${month}-${day}`;
};

const getFullDateKey = (date: Date, timezone: string): string => {
    return date.toLocaleDateString('en-CA', { timeZone: timezone });
};

const getDayOfWeek = (date: Date, timezone: string): string => {
    return new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        weekday: 'long',
    })
        .format(date)
        .toLowerCase();
};

// Get today's schedule info (shared logic)
const getTodayScheduleInfo = (config: CallCenterConfig): TodayScheduleInfo => {
    const now = new Date();
    const { timezone, displayCutoffMinutes, regularHours, holidayHours } = config;

    const dateKey = getDateKey(now, timezone);
    const fullDateKey = getFullDateKey(now, timezone);
    const dayOfWeek = getDayOfWeek(now, timezone);

    const holidaySchedule = holidayHours[fullDateKey] || holidayHours[dateKey];
    const isHoliday = holidaySchedule !== undefined;
    const schedule = holidaySchedule || regularHours[dayOfWeek as keyof typeof regularHours];

    if (!schedule.isOpen || !schedule.hours) {
        return { schedule, isHoliday, openTime: null, closeTime: null, cutoffTime: null };
    }

    const openTime = parseTime(schedule.hours.open, now, timezone);
    const closeTime = parseTime(schedule.hours.close, now, timezone);
    const cutoffTime = new Date(closeTime.getTime() - displayCutoffMinutes * 60 * 1000);

    return { schedule, isHoliday, openTime, closeTime, cutoffTime };
};

export const useCallCenterHours = (
    configOverrides?: Partial<CallCenterConfig>,
    refreshIntervalMs: number = 60000
): CallCenterStatus => {
    const config = useMemo(
        () => ({
            ...DEFAULT_CONFIG,
            ...configOverrides,
            regularHours: { ...DEFAULT_CONFIG.regularHours, ...configOverrides?.regularHours },
            holidayHours: { ...DEFAULT_CONFIG.holidayHours, ...configOverrides?.holidayHours },
        }),
        [configOverrides]
    );

    const calculateStatus = useCallback((): CallCenterStatus => {
        const { isHoliday, openTime, closeTime, cutoffTime } = getTodayScheduleInfo(config);

        if (!openTime || !closeTime || !cutoffTime) {
            return { isCallCenterOpen: false, isCallCenterClosed: true, isTextChatAvailable: false, isHoliday };
        }

        const now = new Date();
        const isCallCenterOpen = now >= openTime && now < closeTime;
        const isCallCenterClosed = !isCallCenterOpen;
        const isTextChatAvailable = now >= openTime && now < cutoffTime;

        return { isCallCenterOpen, isCallCenterClosed, isTextChatAvailable, isHoliday };
    }, [config]);

    const [status, setStatus] = useState<CallCenterStatus>(calculateStatus);

    useEffect(() => {
        let intervalId: NodeJS.Timeout | null = null;
        let timeoutId: NodeJS.Timeout | null = null;

        const startPolling = () => {
            if (!intervalId) {
                intervalId = setInterval(() => setStatus(calculateStatus()), refreshIntervalMs);
            }
        };

        const schedulePolling = () => {
            const { openTime, closeTime } = getTodayScheduleInfo(config);

            if (!openTime || !closeTime) return;

            const now = new Date();
            const oneHourBeforeOpen = new Date(openTime.getTime() - 60 * 60 * 1000);
            const oneHourBeforeClose = new Date(closeTime.getTime() - 60 * 60 * 1000);

            const isWithinOpenWindow = now >= oneHourBeforeOpen && now < openTime;
            const isWithinCloseWindow = now >= oneHourBeforeClose && now < closeTime;

            if (isWithinOpenWindow || isWithinCloseWindow) {
                // Already within 1 hour of opening or closing, start polling immediately
                startPolling();
            } else if (now < oneHourBeforeOpen) {
                // Schedule polling to start 1 hour before open
                const msUntilPollingStart = oneHourBeforeOpen.getTime() - now.getTime();
                timeoutId = setTimeout(() => {
                    setStatus(calculateStatus());
                    startPolling();
                }, msUntilPollingStart);
            } else if (now >= openTime && now < oneHourBeforeClose) {
                // Currently open but not within 1 hour of close, schedule for close window
                const msUntilPollingStart = oneHourBeforeClose.getTime() - now.getTime();
                timeoutId = setTimeout(() => {
                    setStatus(calculateStatus());
                    startPolling();
                }, msUntilPollingStart);
            }
        };

        schedulePolling();

        return () => {
            if (intervalId) clearInterval(intervalId);
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, [calculateStatus, refreshIntervalMs, config]);

    return status;
};
